import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { GuideStatus } from "@mapmycustomers/shared";

import WizardDescriptor, { WizardStepsStatus } from "@app/component/flows/types/WizardDescriptor";
import getNextGuideStatus from "@app/component/flows/utils/getNextGuideStatus";
import { SingleStepWizard } from "@app/component/wizard";
import { getFlowStatuses } from "@app/store/iam";
import { updateFlowStatus } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import useGuides from "@app/util/contexts/useGuides";

import MobileWizardStep from "./MobileGuide/MobileWizardStep";
import styles from "./MobileGuideRunner.module.scss";

export interface MobileGuideRunnerProps {
  descriptor: WizardDescriptor;
}

type Props = MobileGuideRunnerProps &
  ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

const MobileGuideRunner = ({ descriptor, onUpdateFlowStatus, status }: Props) => {
  const { getGuideStatus, hideGuide, updateGuideStatus, visibility } = useGuides();

  const handleStatusChange = useCallback(
    (status: GuideStatus) => {
      const guideStatus = getGuideStatus(descriptor.id);
      if (guideStatus) {
        const newGuideStatus = getNextGuideStatus(guideStatus.status, status);
        if (newGuideStatus !== guideStatus.status) {
          updateGuideStatus({ ...guideStatus, status: newGuideStatus });
        }
      } else {
        console.log("tours: guide status not found for slug", descriptor.id);
      }
    },
    [descriptor.id, getGuideStatus, updateGuideStatus]
  );

  useEffect(
    () => {
      if (visibility[descriptor.id]) {
        handleStatusChange(GuideStatus.PROGRESS);
      }
    },
    // we don't need to specify visibility and descriptor.id separately,
    // better keep it all in one
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibility[descriptor.id], handleStatusChange]
  );

  const handleHide = useCallback(() => {
    hideGuide(descriptor.id);
  }, [hideGuide, descriptor.id]);

  const handleFinish = useCallback(() => {
    // mark last step as complete
    const step = descriptor.steps.at(-1);
    if (step) {
      const newStatus: WizardStepsStatus = {
        ...(status ?? {}),
        [step.id]: true,
      };
      onUpdateFlowStatus({ slug: descriptor.id, status: newStatus });
    }

    handleStatusChange(GuideStatus.COMPLETED);
    handleHide();
  }, [descriptor.steps, descriptor.id, handleStatusChange, handleHide, status, onUpdateFlowStatus]);

  const handleStepChange = useCallback(
    (prevIndex: number, nextIndex: number) => {
      const step = descriptor.steps[prevIndex];
      // if moved to the next screen...
      if (nextIndex > prevIndex && step) {
        // ...mark prevIndex as complete
        const newStatus: WizardStepsStatus = {
          ...(status ?? {}),
          [step.id]: true,
        };
        onUpdateFlowStatus({ slug: descriptor.id, status: newStatus });
      }
    },
    [descriptor.id, descriptor.steps, onUpdateFlowStatus, status]
  );

  return visibility[descriptor.id] ? (
    <SingleStepWizard<WizardDescriptor>
      className={styles.container}
      initialState={descriptor}
      onFinish={handleFinish}
      onHide={handleHide}
      onStepChange={handleStepChange}
      step={MobileWizardStep}
      stepsCount={descriptor.steps.length}
      width="60rem"
    />
  ) : null;
};

const mapStateToProps = (state: RootState, { descriptor }: MobileGuideRunnerProps) => ({
  status: getFlowStatuses(state)[descriptor.id] as undefined | WizardStepsStatus,
});

const mapDispatchToProps = {
  onUpdateFlowStatus: updateFlowStatus.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(MobileGuideRunner);
